<script setup lang="ts">
import type { FeaturedVideosClusterCard } from '~/core/entities/property';

import { useVideoCardPlayer } from '~/components/VideoCard/useVideoCardPlayer';

import ClusterPreviewImages from './ClusterPreviewImages.vue';

const props = withDefaults(
	defineProps<{
		cluster: FeaturedVideosClusterCard;
		index: number;
		isActive?: boolean;
		hoverToPlay?: boolean;
	}>(),
	{
		isActive: false,
		hoverToPlay: true
	}
);

const emit = defineEmits<{
	click: [];
}>();

const videoUrl = computed(() => props.cluster.cardVideo?.url ?? null);
const thumbnailUrl = computed(
	() =>
		props.cluster.cardVideo?.thumbnail?.trim() ||
		props.cluster.previewImages[0] ||
		null
);
const formattedCount = computed(() =>
	props.cluster.count === 1 ? '1 imóvel' : `${props.cluster.count} imóveis`
);

const { cardRef, videoRef, isVideoPlaying, isVisible, isMuted } =
	useVideoCardPlayer({
		videoUrl,
		isActive: computed(() => props.isActive),
		hoverToPlay: computed(() => props.hoverToPlay),
		index: computed(() => props.index),
		playerRootMargin: '80px'
	});
</script>

<template>
	<div
		ref="cardRef"
		class="group relative aspect-[206/318] size-full overflow-hidden bg-primary-light-4"
	>
		<div
			:class="[
				'absolute inset-0 transition-[transform,opacity] duration-300',
				isVideoPlaying ? 'scale-105 opacity-0' : 'scale-100 opacity-100'
			]"
		>
			<NuxtImg
				v-if="thumbnailUrl"
				:src="thumbnailUrl"
				alt=""
				:class="[
					'size-full object-cover',
					props.cluster.cardVideo ? 'scale-[1.15]' : ''
				]"
				width="412"
				height="636"
				sizes="206px"
				format="webp"
				quality="85"
				:loading="props.index < 4 ? 'eager' : 'lazy'"
			/>
			<div v-else class="size-full bg-primary-light-4" />
		</div>

		<video
			v-if="isVisible"
			ref="videoRef"
			:class="[
				'pointer-events-none absolute inset-0 size-full scale-[1.15] object-cover transition-opacity duration-300',
				isVideoPlaying ? 'opacity-100' : 'opacity-0'
			]"
			:muted="isMuted"
			playsinline
			loop
		/>

		<div
			class="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.6)_0%,rgba(0,0,0,0)_50%,rgba(0,0,0,0.6)_100%)]"
		/>

		<div
			class="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-between p-3 text-center lg:px-5 lg:pb-6 lg:pt-5"
		>
			<div class="flex w-full flex-col items-center">
				<span
					class="text-sm font-medium leading-5 text-white [text-shadow:0_1px_3px_rgba(0,0,0,0.15)] lg:text-base lg:leading-[26px]"
				>
					{{ props.cluster.cardTitle }}
				</span>
			</div>

			<div
				data-test="cluster-footer"
				class="flex w-full flex-col items-center justify-center gap-3 lg:gap-4"
			>
				<ClusterPreviewImages
					:images="props.cluster.previewImages"
					:class="[
						'transition-opacity duration-200',
						props.isActive
							? 'opacity-100'
							: 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100'
					]"
				/>

				<span
					data-test="cluster-count"
					class="rounded-[2px] border border-white/10 bg-[rgba(102,102,102,0.3)] px-1.5 py-0.5 text-xs font-medium leading-[1.5] text-white backdrop-blur-[6px]"
				>
					{{ formattedCount }}
				</span>
			</div>
		</div>

		<button
			type="button"
			class="absolute inset-0 z-20 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-white"
			:aria-label="`Explorar ${props.cluster.cardTitle}, ${formattedCount}, em Shorts`"
			@click="emit('click')"
		>
			<span class="sr-only">Abrir cluster em Shorts</span>
		</button>
	</div>
</template>
